import React from 'react';
import clsx from 'clsx';

import IconSlash from '@/assets/icons/icon-slash.svg?react';
import type { EngagementMetric } from '@/components/analytics/types';
import { ListItemRow } from '@/components/analytics/ListItemRow/ListItemRow';

interface EngagementMetricsProps {
  metrics?: EngagementMetric[];
  /** Whether the card has a selected (focused) outline */
  selected?: boolean;
  onSelect?: () => void;
  className?: string;
}

export const EngagementMetrics: React.FC<EngagementMetricsProps> = ({
  metrics,
  selected = false,
  onSelect,
  className,
}) => {
  const hasData = metrics && metrics.length > 0;

  return (
    <div
      className={clsx(
        'bg-neutral-0 flex min-h-96 flex-col items-start justify-start gap-1 self-stretch overflow-hidden rounded-xl pb-3 shadow-[0px_4px_12px_0px_rgba(20,27,36,0.04)] outline-1 -outline-offset-1',
        selected ? 'outline-indigo-500' : 'outline-neutral-100',
        className,
      )}
      onClick={onSelect}
      role={onSelect ? 'button' : undefined}
      tabIndex={onSelect ? 0 : undefined}
    >
      {/* Header */}
      <div className="bg-neutral-0 flex h-12 flex-col items-start justify-center self-stretch">
        <div className="inline-flex flex-1 items-center justify-start gap-2 self-stretch rounded-tl-md rounded-tr-md py-2 pr-2 pl-4">
          <span className="text-xs leading-5 font-semibold text-neutral-900">Engagement</span>
        </div>
      </div>

      {/* Content */}
      {!hasData ? (
        <div className="flex flex-1 self-stretch overflow-hidden px-3">
          <div className="flex flex-1 flex-col items-center justify-center gap-1 rounded-xl bg-indigo-600/10 px-4 py-7">
            <IconSlash className="h-6 w-6 text-indigo-400" />
            <span className="text-center text-[10px] leading-5 font-semibold text-neutral-500">
              No Engagement data yet
            </span>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-start justify-start self-stretch">
          <div className="flex flex-col items-start justify-start gap-2 self-stretch">
            {metrics.map((metric) => (
              <ListItemRow
                key={metric.label}
                label={metric.label}
                value={metric.value}
                change={metric.change}
                changeTrend={metric.changeTrend}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
